import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config';
import logo from '../../assets/Logo - Side.png';
import './CheckoutPage.css';


// Initialize Stripe
let stripePromise = null;
const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/config`);
      const config = await response.json();
      if (config.publishableKey) {
        stripePromise = loadStripe(config.publishableKey);
      }
    } catch (error) {
      console.error('Error loading Stripe config:', error);
    }
  }
  return stripePromise;
};

// Payment Form Component
const CheckoutForm = ({ payment, clientSecret, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: payment?.tenant_email || '',
    phone: ''
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please wait...');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/${payment.id}/success`,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name || undefined,
              email: billingDetails.email || undefined,
              phone: billingDetails.phone || undefined,
            }
          }
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded
        onSuccess(paymentIntent);
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // Payment requires additional action (like 3D Secure)
        const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
        if (actionError) {
          setError(actionError.message);
          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred processing your payment');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="billing-details-section">
        <h3>Billing Information</h3>
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            id="name"
            type="text"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
            placeholder="John Doe"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({...billingDetails, email: e.target.value})}
            placeholder="john@example.com"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone (Optional)</label>
          <input
            id="phone"
            type="tel"
            value={billingDetails.phone}
            onChange={(e) => setBillingDetails({...billingDetails, phone: e.target.value})}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div className="payment-element-section">
        <h3>Payment Information</h3>
        <div className="payment-element-wrapper">
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodTypes: ['card', 'us_bank_account']
            }}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="submit-payment-btn"
      >
        {processing ? (
          <>
            <span className="spinner"></span>
            Processing...
          </>
        ) : (
          `Pay $${(payment?.amount || 0).toFixed(2)}`
        )}
      </button>

      <div className="security-notice">
        <span className="lock-icon">🔒</span>
        <span>Your payment information is securely processed by Stripe. We never store your card details.</span>
      </div>
    </form>
  );
};

// Main Checkout Page Component
const CheckoutPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    loadPaymentData();
  }, [paymentId]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Initialize Stripe
      const stripe = await getStripePromise();
      setStripePromise(stripe);

      // Fetch payment details - use rental-payments endpoint and filter
      const paymentResponse = await fetch(`${API_BASE_URL}/api/financial/rental-payments`);
      if (!paymentResponse.ok) {
        throw new Error('Failed to fetch payment information');
      }

      const paymentsData = await paymentResponse.json();
      const paymentData = Array.isArray(paymentsData) 
        ? paymentsData.find(p => p.id === parseInt(paymentId)) 
        : null;
      
      if (!paymentData) {
        throw new Error('Payment not found');
      }

      setPayment(paymentData);

      // Create payment intent
      const intentResponse = await fetch(`${API_BASE_URL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_id: paymentData.id,
          amount: paymentData.amount,
          payment_type: 'both' // Support both card and ACH
        })
      });
      
      const intentData = await intentResponse.json();
      
      if (!intentResponse.ok) {
        throw new Error(intentData.error || 'Failed to create payment intent');
      }

      setClientSecret(intentData.client_secret);
    } catch (err) {
      console.error('Error loading payment:', err);
      setError(err.message || 'Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setPaymentSuccess(true);
    // Redirect to success page after a brief delay
    setTimeout(() => {
      navigate(`/checkout/${paymentId}/success`);
    }, 2000);
  };

  const handleCancel = () => {
    const returnPath = searchParams.get('return') || '/financial';
    navigate(returnPath);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading payment information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Payment Error</h2>
            <p>{error}</p>
            <button onClick={handleCancel} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p>Your payment has been processed successfully.</p>
            <button onClick={() => navigate(`/checkout/${paymentId}/success`)} className="btn-primary">
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <div className="logo-section">
            <img 
              src={logo} 
              alt="RentafAI Logo" 
              className="checkout-logo"
              onError={(e) => {
                // Fallback if logo not found
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'block';
                }
              }}
            />
            <span className="logo-text" style={{ display: 'none' }}>RentafAI</span>
          </div>
          <button onClick={handleCancel} className="close-btn">×</button>
        </div>

        <div className="checkout-content">
          <div className="checkout-main">
            <h1>Complete Your Payment</h1>
            
            <div className="payment-summary">
              <div className="summary-row">
                <span className="summary-label">Tenant:</span>
                <span className="summary-value">{payment?.tenant_name || 'N/A'}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Property:</span>
                <span className="summary-value">{payment?.property_name || 'N/A'}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Due Date:</span>
                <span className="summary-value">
                  {payment?.due_date ? new Date(payment.due_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="summary-row total-row">
                <span className="summary-label">Total Amount:</span>
                <span className="summary-value total-amount">{formatCurrency(payment?.amount)}</span>
              </div>
            </div>

            {stripePromise && clientSecret && (
              <Elements 
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#1457f5',
                      colorBackground: '#ffffff',
                      colorText: '#1e293b',
                      colorDanger: '#ef4444',
                      fontFamily: 'system-ui, sans-serif',
                      borderRadius: '8px',
                    },
                  },
                }}
              >
                <CheckoutForm 
                  payment={payment}
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                />
              </Elements>
            )}
          </div>

          <div className="checkout-sidebar">
            <div className="security-badges">
              <div className="badge">
                <span className="badge-icon">🔒</span>
                <span className="badge-text">Secure Payment</span>
              </div>
              <div className="badge">
                <span className="badge-icon">✓</span>
                <span className="badge-text">PCI Compliant</span>
              </div>
              <div className="badge">
                <span className="badge-icon">💳</span>
                <span className="badge-text">Card & ACH Accepted</span>
              </div>
            </div>

            <div className="payment-help">
              <h3>Need Help?</h3>
              <p>If you have any questions about your payment, please contact support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;

